import { createSlice, PayloadAction } from "@reduxjs/toolkit";

// Define the state type for tab management
interface TabState {
  activeTab: string;
  tabs: { [key: string]: any };
  tabNames: string[];
  isLoading?: boolean;
  showTabs: boolean;
  disabledTabs: string[];
}


// Set initial state with default tab
const initialState: TabState = {
  activeTab: "",
  tabs: {},
  tabNames: [],
  showTabs: true,
  disabledTabs: [],
};

const tabSlice = createSlice({
  name: "tabs",
  initialState,
  reducers: {
    // Set active tab
    _setActiveTab: (state: TabState, action: PayloadAction<string>) => {
      state.activeTab = action.payload;
    },

    addTabs: (state: TabState, action: PayloadAction<object>) => {
      state.tabs = action.payload;
      state.tabNames = Object.keys(action.payload);
      // The `tab` URL param is overloaded: pages with multiple page-level tabs
      // use it to pick the active tab, but deep-links (e.g. JVVNL search's
      // `tab=forecast`/`tab=actual`) use it for an in-page sub-tab the page reads
      // itself. Only honor it as a page-level tab when it actually names one of
      // this page's tabs — otherwise fall back to the first tab. Without this
      // guard a `tab=forecast` deep-link sets activeTab to a non-existent key and
      // the page renders "Invalid tab selected".
      const urlTab = new URLSearchParams(window.location.search).get("tab");
      state.activeTab =
        urlTab && Object.prototype.hasOwnProperty.call(action.payload, urlTab)
          ? urlTab
          : Object.keys(action.payload)[0];
    },
    setShowTabs: (state, action: PayloadAction<boolean>) => {
      state.showTabs = action.payload;
    },
    setDisabledTabs: (state, action: PayloadAction<string[]>) => {
      state.disabledTabs = action.payload;
      // If the current active tab is disabled, switch to the first enabled tab
      if (action.payload.includes(state.activeTab) && state.tabNames.length > 0) {
        const firstEnabledTab = state.tabNames.find(tab => !action.payload.includes(tab));
        if (firstEnabledTab) {
          state.activeTab = firstEnabledTab;
        }
      }
    },
  },
});

export const { _setActiveTab, addTabs, setShowTabs, setDisabledTabs } = tabSlice.actions;

// Export thunk for setActiveTab to support page-level transitions
export const setActiveTab = (payload: string): any => (dispatch: any) => {
  document.body.classList.add("page-tab-exiting");

  setTimeout(() => {
    dispatch(_setActiveTab(payload));

    document.body.classList.remove("page-tab-exiting");
    document.body.classList.add("page-tab-entering");

    setTimeout(() => {
      document.body.classList.remove("page-tab-entering");
    }, 300);
  }, 250);
};

export default tabSlice.reducer;
